/**
 * navHeader — header behaviour enhancement (Alpine CSP build).
 *
 * On a transparent header, watches an invisible scroll sentinel near the
 * top of the document. While the sentinel is in view the page is near the
 * top → the header stays transparent over the hero; once the sentinel has
 * scrolled out of view the `.is-solid` class is added and the CSS
 * cross-fades the header to its solid look. A default (non-transparent)
 * header does nothing — it is already solid.
 *
 * Why a sentinel: the header itself is `position: sticky` and never
 * changes viewport position, so it cannot observe its own scroll. The
 * sentinel sits in normal document flow and does.
 *
 * closeMenus() blurs the active element so a CSS :focus-within dropdown
 * closes when the user presses Escape.
 */
export default function navHeader() {
    return {
        _observer: null,
        _sentinel: null,

        init() {
            if (!this.$root.classList.contains('is-transparent')) {
                return;
            }

            // Invisible marker at the very top of the document, one header
            // tall. It scrolls with the page; once it leaves the viewport
            // the page has scrolled far enough to solidify the header.
            this._sentinel = document.createElement('div');
            this._sentinel.setAttribute('aria-hidden', 'true');
            this._sentinel.style.position = 'absolute';
            this._sentinel.style.top = '0';
            this._sentinel.style.left = '0';
            this._sentinel.style.width = '1px';
            this._sentinel.style.height = `${this.$root.offsetHeight}px`;
            this._sentinel.style.pointerEvents = 'none';
            document.body.prepend(this._sentinel);

            this._observer = new IntersectionObserver(([entry]) => {
                this.$root.classList.toggle('is-solid', !entry.isIntersecting);
            });

            this._observer.observe(this._sentinel);
        },

        closeMenus() {
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }
        },

        destroy() {
            this._observer?.disconnect();
            this._sentinel?.remove();
        },
    };
}
